import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
} from "@angular/forms";

@Component({
    selector: "app-popup-modal",
    templateUrl: "./popup-modal.component.html",
    styleUrls: ["./popup-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupModalComponent implements OnInit, OnChanges {
  @Input() title: string = "Popup Title";
  @Input() tableHeaders: { label: string; options: string[] }[] = [];
  @Input() pidList: any[] = [];
  @Input() pidDetails: any = {};
  @Input() isEditMode: boolean = false;
  @Input() existingData: any = null;

  @Output() close = new EventEmitter<void>();
  @Output() submit = new EventEmitter<any>();

  form: UntypedFormGroup;
  primarySelection: string = "";
  pendingEditData: any = null; // Store data to populate after PID selection
  isSettingPidProgrammatically = false; // Flag to prevent recursive calls

  constructor(private fb: UntypedFormBuilder) {
    this.form = this.fb.group({
      pidName: ["", Validators.required],
    });
  }

  ngOnInit() {
    // Disable PID name control if in edit mode
    if (this.isEditMode) {
      const pidNameControl = this.form.get("pidName");
      if (pidNameControl) {
        pidNameControl.disable();
      }
    }

    // Handle edit mode - store existing data for later population
    if (this.isEditMode && this.existingData) {
      this.pendingEditData = { ...this.existingData };

      // Try to set PID immediately if available
      this.setPidFromExistingData();

      // If PID list is not available yet, set up polling
      if (!this.pidList || this.pidList.length === 0) {
        console.error("PID list is not available");
      }
    }

    // Listen for changes on the pidName form control
    const pidNameControl = this.form.get("pidName");
    if (pidNameControl) {
      pidNameControl.valueChanges.subscribe((selectedValue) => {
        // Skip if we're setting the PID programmatically
        if (this.isSettingPidProgrammatically) {
          return;
        }

        if (
          selectedValue !== null &&
          selectedValue !== undefined &&
          selectedValue !== ""
        ) {
          this.onPidChange(selectedValue);
        }
      });
    }
  }

  private setPidFromExistingData() {
    if (!this.isEditMode || !this.existingData || !this.existingData.pidName) {
      return;
    }

    const target = this.existingData.pidName;
    const targetLow = target.toLowerCase();

    const existingPid = this.pidList.find((pid) => {
      const values = [pid.label, pid.value, pid.pidId, pid.pidDescription];
      return values.some(
        (value) =>
          value === target ||
          (value && value.toString().toLowerCase() === targetLow)
      );
    });

    if (existingPid) {
      // Debug the PID list to find the exact match
      const matchingPid = this.pidList.find((p) => {
        // Try multiple matching strategies
        const valueMatch =
          p.value === existingPid.value || p.value === existingPid.pidId;
        const labelMatch =
          p.label === existingPid.label || p.label === existingPid.pidId;
        const stringValueMatch =
          p.value.toString() ===
            (existingPid.value && existingPid.value.toString()) ||
          p.value.toString() ===
            (existingPid.pidId && existingPid.pidId.toString());
        const stringLabelMatch =
          p.label.toString() ===
            (existingPid.label && existingPid.label.toString()) ||
          p.label.toString() ===
            (existingPid.pidId && existingPid.pidId.toString());

        return valueMatch || labelMatch || stringValueMatch || stringLabelMatch;
      });

      // For the dropdown, we need to use the value that matches the PID list format
      // The dropdown expects the 'value' property from the PID list
      const pidValue = matchingPid
        ? matchingPid.value
        : existingPid.value || existingPid.pidId || existingPid.label;
      // Set flag to prevent recursive calls
      this.isSettingPidProgrammatically = true;

      // Set the primarySelection first so fields can be created
      // Use the value for PID details lookup
      this.primarySelection =
        existingPid.value || existingPid.pidId || existingPid.label;
      this.form.patchValue({ pidName: pidValue });
      this.createFieldsForPid(pidValue);

      // Reset flag after a short delay
      setTimeout(() => {
        this.isSettingPidProgrammatically = false;
      }, 100);
    }
  }

  get currentFields() {
    if (!this.primarySelection || !this.pidDetails[this.primarySelection]) {
      return [];
    }

    const fields = this.pidDetails[this.primarySelection].fields || [];
    return fields;
  }

  onPidChange(pidValue: string) {
    // Update the current selection so currentFields returns the right metadata
    this.primarySelection = pidValue;

    // Re-create form controls for the selected PID
    this.createFieldsForPid(pidValue);

    // In edit mode, immediately populate the form once controls exist
    if (this.isEditMode && this.pendingEditData) {
      this.populateFormWithExistingData();
    }
  }

  get isFormValid(): boolean {
    // Check if pidName is selected
    // const pidNameValue = this.form.get("pidName")?.value;
    const pidNameControl = this.form.get("pidName");
    const pidNameValue = pidNameControl ? pidNameControl.value : null;

    if (!pidNameValue) {
      return false;
    }

    // Check if all form controls are valid
    return this.form.valid;
  }

  onSubmit() {
    if (this.form.valid) {
      const formData = this.form.value;

      // Debug individual control values and states
      Object.keys(this.form.controls).forEach((controlName) => {
        const control = this.form.get(controlName);
      });

      // Manually collect values from disabled controls since they're excluded from form.value
      const completeFormData = { ...formData };
      Object.keys(this.form.controls).forEach((controlName) => {
        const control = this.form.get(controlName);
        if (
          control &&
          control.disabled &&
          control.value !== undefined &&
          control.value !== null
        ) {
          completeFormData[controlName] = control.value;
        }
      });

      // Get the selected PID from pidList
      const selectedPid = this.pidList.find(
        (pid) => pid.value === formData.pidName
      );
      const submitData: any = {
        ...completeFormData,
        pidName: selectedPid ? selectedPid.label : formData.pidName,
        pidId: selectedPid
          ? selectedPid.pidId || selectedPid.value
          : formData.pidName,
      };

      // Pass cross-PID equivalence information to the parent ONLY when a new
      // PID is being added. During edit mode the PID itself cannot change, so
      // this data is unnecessary and—because the field is disabled—can
      // interfere with the save logic upstream.
      if (!this.isEditMode && selectedPid && selectedPid.crossPidsValidations) {
        submitData.crossPidsValidations = selectedPid.crossPidsValidations;
      }
      this.submit.emit(submitData);
      this.resetForm();
      this.close.emit();
    }
  }

  resetForm() {
    // Reset the form to initial state
    this.form.reset();
    this.primarySelection = "";
    this.pendingEditData = null; // Clear pending edit data

    // Remove all controls except pidName
    Object.keys(this.form.controls).forEach((key) => {
      if (key !== "pidName") {
        this.form.removeControl(key);
      }
    });

    // Re-enable PID name control if it was disabled
    const pidNameControl = this.form.get("pidName");
    if (pidNameControl && pidNameControl.disabled) {
      pidNameControl.enable();
    }
  }

  populateFormWithExistingData() {
    if (!this.pendingEditData || !this.form) {
      return;
    }

    // Create a patch object with existing data
    const patchData: any = {};

    // Iterate through all form controls and populate with existing data
    Object.keys(this.form.controls).forEach((controlName) => {
      if (
        this.pendingEditData[controlName] !== undefined &&
        this.pendingEditData[controlName] !== null
      ) {
        let value = this.pendingEditData[controlName];

        // Handle boolean values that might be stored as "1"/"0"
        if (
          typeof value === "string" &&
          (value === "1" || value === "0") &&
          this.isBooleanField(controlName)
        ) {
          value = value === "1";
        }

        patchData[controlName] = value;
      }
    });

    this.form.patchValue(patchData);

    // Clear the pending data after population
    this.pendingEditData = null;
  }

  private isBooleanField(name: string): boolean {
    const field = this.currentFields.find((field) => field.fieldName === name);
    return field.fieldType === "toggle";
  }

  private createFieldsForPid(pidValue: string) {
    // Remove all old controls except pidName
    Object.keys(this.form.controls).forEach((key) => {
      if (key !== "pidName") {
        this.form.removeControl(key);
      }
    });

    const fields = this.currentFields;

    fields.forEach((field) => {
      // Handle toggle group
      if (field.fieldType === "toggle-group" && field.toggles) {
        field.toggles.forEach((toggleField: any) => {
          const validators = [];
          if (toggleField.required) validators.push(Validators.requiredTrue);
          this.form.addControl(
            toggleField.fieldName,
            new UntypedFormControl(toggleField.value || false, validators)
          );
        });
        return;
      }

      const validators = [];
      // Always prevent empty values for any field when adding new PIDs
      validators.push(Validators.required);

      if (field.fieldType === "number") {
        if (field.min !== undefined) validators.push(Validators.min(field.min));
        if (field.max !== undefined) validators.push(Validators.max(field.max));
      }

      // Check for default values in order: defaultValue, value, then min for number fields
      let initialValue: any = null;

      // First check for explicit default value
      if (field.defaultValue !== undefined && field.defaultValue !== null) {
        initialValue = field.defaultValue;
      }
      // Then check for field value
      else if (field.value !== undefined && field.value !== null) {
        initialValue = field.value;
      }
      // For number fields, only use min if no default value is present
      else if (field.fieldType === "number" && field.min !== undefined) {
        initialValue = field.min;
      }
      // Fallback to empty string for other field types
      else {
        initialValue = "";
      }

      this.form.addControl(
        field.fieldName,
        new UntypedFormControl(initialValue, validators)
      );
    });

    // All controls are now present – safe to patch
    if (this.isEditMode && this.pendingEditData) {
      this.populateFormWithExistingData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle changes to isEditMode
    if (changes["isEditMode"]) {
      const pidNameControl = this.form.get("pidName");
      if (pidNameControl) {
        if (this.isEditMode) {
          pidNameControl.disable();
        } else {
          pidNameControl.enable();
        }
      }
    }

    if (
      changes["pidList"] &&
      this.isEditMode &&
      this.existingData &&
      this.existingData.pidName
    ) {
      this.setPidFromExistingData();
    }
  }
}
